<template>
	<figure class="cms-post-image" :class="['cms-post-image--' + mode, mode === 'overlay' ? 'cms-post-image--' + (textPosition || 'bottom-left') : '']" :style="figureStyle">
		<div class="cms-post-image__frame" :style="frameStyle">
			<img v-if="image.src" class="cms-post-image__pic" v-bind="picAttrs" />
			<div v-else class="cms-post-image__empty">
				<i class="fa fa-picture-o"></i>
				<span>The post's featured picture shows here</span>
			</div>
			<div v-if="mode === 'overlay'" class="cms-post-image__shade" :style="{ background: overlayColor || 'rgba(0,0,0,0.45)' }"></div>
			<div v-if="mode === 'overlay'" class="cms-post-image__text">
				<post-meta-block v-if="isOn(showMeta)" class="cms-post-image__meta" :post="resolved" :show-reading-time="false" size="normal" :align="overlayAlign"></post-meta-block>
				<h1 class="cms-post-image__title" :style="titleStyle">{{ resolved.websitePostTitle }}</h1>
			</div>
			<small v-if="credit && mode === 'overlay'" class="cms-post-image__credit cms-post-image__credit--inside">{{ credit }}</small>
		</div>
		<figcaption v-if="mode === 'image' && (caption || credit)" class="cms-post-image__caption">
			<span v-if="caption">{{ caption }}</span>
			<small v-if="credit" class="cms-post-image__credit">{{ credit }}</small>
		</figcaption>
	</figure>
</template>

<script>
/**
 * PostFeatureImageBlock.vue
 * ─────────────────────────
 * The article's featured picture, for Article templates. Two layouts:
 *   image   — the picture alone, in a chosen shape, credit and caption under it
 *   overlay — a large photo with the title (and meta) over a shade, text at the
 *             bottom-left, bottom-center or center
 * Reads the post as the other Post blocks do (blocks/shared/postContext.js);
 * the picture gets srcset/sizes through imgAttrs like the Post List.
 */
import { isTrue } from '@/js/utilities/boolean/conditional';
import { imgAttrs } from '@/websites/cms/blocks/shared/imageSrc.js';
import { creditLine } from '@/websites/cms/blocks/shared/imageCredit.js';
import { buildHeadingLevelStyle } from '@/websites/cms/blocks/headingBlock/headingLevelStyle.js';
import { resolvePost, postImage, pictureCandidates } from '@/websites/cms/blocks/shared/postContext.js';
import PostMetaBlock from '@/websites/cms/blocks/postMetaBlock/postMetaBlock.vue';

var ASPECTS = { '21:9': '21 / 9', '16:9': '16 / 9', '3:2': '3 / 2', '4:3': '4 / 3', '1:1': '1 / 1' };

export default {
	name: 'PostFeatureImageBlock',

	components: { PostMetaBlock: PostMetaBlock },

	inject: {
		cmsPostPreview: { default: null },
		siteSettings: { default: function () { return {}; } }
	},

	props: {
		layout: { type: String, default: 'image' },
		aspect: { type: String, default: 'natural' },
		height: { type: String, default: '70vh' },
		textPosition: { type: String, default: 'bottom-left' },
		overlayColor: { type: String, default: 'rgba(0,0,0,0.45)' },
		showMeta: { type: [Boolean, String], default: true },
		showCredit: { type: [Boolean, String], default: true },
		showCaption: { type: [Boolean, String], default: false },
		post: { type: Object, default: null }
	},

	computed: {
		resolved: function () {
			return resolvePost(this.post, this.cmsPostPreview);
		},

		mode: function () {
			return this.layout === 'overlay' ? 'overlay' : 'image';
		},

		image: function () {
			return postImage(this.resolved);
		},

		picAttrs: function () {
			var pageW = parseInt((this.siteSettings && this.siteSettings.pageWidth) || '', 10) || 1280;
			var attrs = imgAttrs(this.image.src, {
				widths: [640, 960, 1280, 1920, 2560],
				fallbackWidth: 1920,
				sizes: this.mode === 'overlay' ? '100vw' : 'min(100vw, ' + pageW + 'px)',
				priority: true,
				candidates: pictureCandidates(this.resolved)
			});
			attrs.alt = this.resolved.websitePostTitle || '';
			return attrs;
		},

		credit: function () {
			if (!isTrue(this.showCredit)) return '';
			return creditLine(this.resolved.featureImageCredit, this.siteSettings);
		},

		caption: function () {
			return isTrue(this.showCaption) ? (this.resolved.websitePostExcerpt || '') : '';
		},

		overlayAlign: function () {
			return this.textPosition === 'bottom-center' || this.textPosition === 'center' ? 'center' : 'left';
		},

		figureStyle: function () {
			return this.mode === 'overlay' ? { '--cms-post-image-height': this.height || '70vh' } : {};
		},

		frameStyle: function () {
			if (this.mode === 'overlay') return {};
			var ratio = ASPECTS[this.aspect];
			return ratio ? { aspectRatio: ratio } : {};
		},

		titleStyle: function () {
			var s = buildHeadingLevelStyle('h1') || {};
			s.color = '#FFFFFF';
			s.textAlign = this.overlayAlign;
			return s;
		}
	},

	methods: {
		isOn: isTrue
	}
};
</script>

<style scoped>
.cms-post-image { margin: 0; width: 100%; max-width: 100%; }
.cms-post-image__frame { position: relative; width: 100%; overflow: hidden; background: #F4F4F5; }
.cms-post-image__pic { display: block; width: 100%; height: auto; }
.cms-post-image__frame[style*="aspect-ratio"] .cms-post-image__pic { height: 100%; object-fit: cover; }
.cms-post-image__empty {
	display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .5rem;
	min-height: 220px; color: #999; border: 2px dashed #DDDEE0; border-radius: 4px;
}
.cms-post-image__empty .fa { font-size: 2em; }
.cms-post-image__caption { padding: .5rem 0 0; font-size: .875em; color: var(--site-body-color, #5b6472); display: flex; justify-content: space-between; gap: 1rem; }
.cms-post-image__credit { font-size: .8em; opacity: .75; white-space: nowrap; }

/* Title over the picture */
.cms-post-image--overlay .cms-post-image__frame { height: var(--cms-post-image-height, 70vh); min-height: 320px; }
.cms-post-image--overlay .cms-post-image__pic { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.cms-post-image--overlay .cms-post-image__empty { position: absolute; inset: 0; min-height: 0; border: 0; }
.cms-post-image__shade { position: absolute; inset: 0; }
.cms-post-image__text {
	position: absolute; left: 0; right: 0; bottom: 0;
	display: flex; flex-direction: column; gap: .75rem;
	padding: clamp(1.25rem, 4vw, 3rem);
	max-width: var(--site-page-width, 1000px); margin: 0 auto;
	color: #FFFFFF;
}
.cms-post-image--center .cms-post-image__text { top: 0; justify-content: center; align-items: center; text-align: center; }
.cms-post-image--bottom-center .cms-post-image__text { align-items: center; text-align: center; }
.cms-post-image__title { margin: 0; color: #FFFFFF; text-shadow: 0 2px 12px rgba(0,0,0,.35); text-wrap: balance; max-width: 22ch; }
.cms-post-image--bottom-center .cms-post-image__title, .cms-post-image--center .cms-post-image__title { max-width: 26ch; }
.cms-post-image__meta { color: rgba(255,255,255,.9); }
.cms-post-image__meta >>> .cms-post-meta__part--author, .cms-post-image__meta >>> .cms-post-meta__part { color: inherit; }
.cms-post-image__credit--inside { position: absolute; right: .75rem; bottom: .5rem; padding: .15rem .45rem; border-radius: 3px; background: rgba(0,0,0,.45); color: #FFF; opacity: 1; }
</style>
